<template>
  <section class="dashboard-page">
    <div class="dashboard-page__header">
      <div class="dashboard-page__heading">
        <div class="dashboard-page__eyebrow">Operations</div>
        <h1 class="dashboard-page__title">Dashboard</h1>
      </div>

      <el-card shadow="never" class="dashboard-range-card">
        <div class="dashboard-range-card__label">Reporting Window</div>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          unlink-panels
          range-separator="to"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="dd MMM yyyy"
          value-format="yyyy-MM-dd"
          :clearable="false"
          class="dashboard-range-card__picker"
          @change="changeDate"
        />
      </el-card>
    </div>

    <el-row :gutter="16" class="dashboard-stats">
      <el-col
        v-for="card in summaryCards"
        :key="card.key"
        :xs="24"
        :sm="12"
        :lg="8"
      >
        <el-card
          shadow="hover"
          class="dashboard-stat"
          :class="`dashboard-stat--${card.tone}`"
        >
          <div class="dashboard-stat__content">
            <div>
              <div class="dashboard-stat__label">{{ card.label }}</div>
              <div class="dashboard-stat__value">{{ card.value }}</div>
            </div>
            <div class="dashboard-stat__icon">
              <i :class="card.icon"></i>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="dashboard-charts">
      <el-col :span="24">
        <section class="dashboard-panel" v-loading="chartLoading">
          <div class="dashboard-panel__header">
            <div>
              <div class="dashboard-panel__eyebrow">Traffic</div>
              <h2 class="dashboard-panel__title">Message Volume Overview</h2>
            </div>
            <el-tag effect="plain" size="small">{{ dateRangeLabel }}</el-tag>
          </div>
          <div class="dashboard-chart dashboard-chart--large">
            <chart-line
              :labels="dataChart.sent.valuesX"
              :datasets="allState"
            />
          </div>
        </section>
      </el-col>

      <el-col
        v-for="panel in chartPanels"
        :key="panel.key"
        :xs="24"
        :md="12"
        :xl="8"
      >
        <section class="dashboard-panel" v-loading="chartLoading">
          <div class="dashboard-panel__header">
            <div>
              <div class="dashboard-panel__eyebrow">
                {{ panel.eyebrow }}
              </div>
              <h2 class="dashboard-panel__title">{{ panel.title }}</h2>
            </div>
            <div class="dashboard-panel__caption">
              {{ panel.caption }}
            </div>
          </div>
          <div class="dashboard-chart dashboard-chart--compact">
            <chart-bar
              :labels="dataChart[panel.key].valuesX"
              :datasets="dataChart[panel.key].valuesY"
              :title="panel.title"
            />
          </div>
        </section>
      </el-col>
    </el-row>

  </section>
</template>

<script>
import ChartLine from "./ChartLine.vue";
import ChartBar from "./ChartBar";
import Utils from "../utils";
import moment from "moment";

const createMessagesState = () => ({
  sent: [],
  receive: [],
  fail: [],
});

const createChartDataset = (label, color) => ({
  label,
  backgroundColor: color,
  borderColor: color,
  data: [],
  fill: false,
  tension: 0,
  borderWidth: 2,
  pointRadius: 3,
  pointHoverRadius: 5,
});

const createChartState = () => ({
  sent: {
    valuesX: [],
    valuesY: [createChartDataset("Sent messages", "#10b981")],
  },
  receive: {
    valuesX: [],
    valuesY: [createChartDataset("Received messages", "#3b82f6")],
  },
  fail: {
    valuesX: [],
    valuesY: [createChartDataset("Failed messages", "#ef4444")],
  },
});

export default {
  name: "dashboard",
  components: {
    ChartLine,
    ChartBar,
  },
  data() {
    return {
      chartLoading: false,
      messages: createMessagesState(),
      dataChart: createChartState(),
      partners: [],
      certificates: [],
      partnerships: [],
      dateRange: [
        moment().startOf("week").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
      ],
      states: ["sent", "receive", "fail"],
      stateMap: {
        sent: [
          "msg_send_start",
          "mdn_send_start",
          "msg_rxd_mdn_sent_ok",
          "msg_rxd_mdn_not_requested_ok",
        ],
        receive: [
          "msg_sent_mdn_received_mic_mismatch",
          "msg_sent_mdn_received_ok",
          "msg_receive_start",
          "mdn_receive_start",
        ],
        fail: [
          "msg_send_exception",
          "msg_receive_exception",
          "mdn_sending_exception",
          "mdn_receiving_exception",
          "msg_send_fail",
          "msg_send_fail_resend_queued",
          "msg_receive_fail",
          "msg_rxd_asyn_mdn_send_fail_resend_queued",
          "mdn_asyn_receive_fail",
          "msg_rxd_mdn_sending_fail",
          "msg_receive_error_sending_mdn_error",
          "msg_sent_mdn_received_error",
        ],
      },
    };
  },
  computed: {
    countPartners() {
      return this.partners.length;
    },
    countCertificates() {
      return this.certificates.length;
    },
    countPartnerships() {
      return this.partnerships.length;
    },
    allState() {
      return this.states.map((state) => this.dataChart[state].valuesY[0]);
    },
    summaryCards() {
      return [
        {
          key: "partners",
          label: "Total Partners",
          value: this.countPartners,
          icon: "el-icon-user-solid",
          tone: "amber",
        },
        {
          key: "connections",
          label: "Total Connections",
          value: this.countPartnerships,
          icon: "el-icon-share",
          tone: "slate",
        },
        {
          key: "certificates",
          label: "Total Certificates",
          value: this.countCertificates,
          icon: "el-icon-tickets",
          tone: "azure",
        },
      ];
    },
    chartPanels() {
      return [
        {
          key: "sent",
          eyebrow: "Outbound",
          title: "Sent Messages",
          caption: "Delivery attempts and successful sends",
        },
        {
          key: "receive",
          eyebrow: "Inbound",
          title: "Received Messages",
          caption: "Inbound payloads and MDN activity",
        },
        {
          key: "fail",
          eyebrow: "Attention",
          title: "Failed Messages",
          caption: "Items needing a retry or investigation",
        },
      ];
    },
    dateRangeLabel() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return "Current reporting window";
      }
      return `${moment(this.dateRange[0]).format("DD MMM YYYY")} to ${moment(
        this.dateRange[1]
      ).format("DD MMM YYYY")}`;
    },
  },
  mounted() {
    this.getListPartners();
    this.getListCertificates();
    this.getListConnections();
    this.getListMessageChart();
  },
  methods: {
    resetChartData() {
      this.messages = createMessagesState();
      this.dataChart = createChartState();
    },
    getField(item, key) {
      if (!item || typeof item !== "object") return null;

      const lowerKey = key.toLowerCase();
      const value = item[key];
      const lowerValue = item[lowerKey];

      if (value !== undefined && value !== null && value !== "") return value;
      if (lowerValue !== undefined && lowerValue !== null && lowerValue !== "") {
        return lowerValue;
      }
      return null;
    },
    async getListPartners() {
      this.partners = await Utils.Crud.getList("partner");
    },
    async getListCertificates() {
      this.certificates = await Utils.Crud.getList("cert");
    },
    async getListConnections() {
      this.partnerships = await Utils.Crud.getList("partnership");
    },
    async getListMessageChart() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.chartLoading = true;
      this.resetChartData();

      try {
        const data = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1],
        };
        const messages = await Utils.Crud.getListChart("messages", data);
        const chartMessages = Array.isArray(messages)
          ? messages.filter((message) => message && typeof message === "object")
          : [];

        chartMessages.forEach((message) => {
          const stateCode = this.getField(message, "STATE");
          const createDate = this.getField(message, "CREATE_DT");
          const messageId = this.getField(message, "MSG_ID");

          if (!stateCode || !createDate) {
            return;
          }

          this.states.forEach((state) => {
            if (this.stateMap[state].includes(stateCode)) {
              this.messages[state].push({
                date: moment(createDate).format("YYYY-MM-DD"),
                name: messageId,
              });
            }
          });
        });

        const start = moment(data.startDate);
        const end = moment(data.endDate);

        while (start.isSameOrBefore(end, "day")) {
          const day = start.format("YYYY-MM-DD");

          this.states.forEach((state) => {
            this.dataChart[state].valuesX.push(day);
            const count = this.messages[state].filter((msg) => msg.date === day)
              .length;
            this.dataChart[state].valuesY[0].data.push(count);
          });

          start.add(1, "days");
        }
      } catch (error) {
        this.$message.error("Unable to load dashboard chart data.");
      } finally {
        this.chartLoading = false;
      }
    },
    changeDate() {
      this.getListMessageChart();
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #111827;
}

.dashboard-page__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.dashboard-page__heading {
  max-width: 760px;
}

.dashboard-page__eyebrow,
.dashboard-panel__eyebrow {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2563eb;
}

.dashboard-page__title {
  margin: 6px 0 0;
  font-size: 34px;
  line-height: 1.05;
  font-weight: 700;
}

.dashboard-range-card {
  width: min(100%, 360px);
  border: 1px solid #dbeafe;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #eff6ff 100%);
}

.dashboard-range-card__label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.dashboard-range-card__picker {
  width: 100%;
}

.dashboard-stats,
.dashboard-charts {
  margin: 0;
}

.dashboard-stat {
  position: relative;
  overflow: hidden;
  border: none;
  border-radius: 18px;
  color: #ffffff;
}

.dashboard-stat::before {
  content: "";
  position: absolute;
  inset: auto -40px -40px auto;
  width: 120px;
  height: 120px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
}

.dashboard-stat .el-card__body {
  padding: 20px;
}

.dashboard-stat--amber {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.dashboard-stat--slate {
  background: linear-gradient(135deg, #111827 0%, #334155 100%);
}

.dashboard-stat--azure {
  background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%);
}

.dashboard-stat__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dashboard-stat__label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.88;
}

.dashboard-stat__value {
  margin-top: 10px;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
}

.dashboard-stat__icon {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  font-size: 26px;
}

.dashboard-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #ffffff;
  padding: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.dashboard-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.dashboard-panel__title {
  margin: 6px 0 0;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
}

.dashboard-panel__caption {
  max-width: 220px;
  text-align: right;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
}

.dashboard-chart {
  flex: 1;
  width: 100%;
  min-height: 0;
}

.dashboard-chart--large {
  height: 340px;
}

.dashboard-chart--compact {
  height: 280px;
}

@media (max-width: 1024px) {
  .dashboard-page__title {
    font-size: 30px;
  }

  .dashboard-panel__caption {
    max-width: 180px;
  }
}

@media (max-width: 768px) {
  .dashboard-page__header,
  .dashboard-panel__header {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-range-card {
    width: 100%;
  }

  .dashboard-panel__caption {
    max-width: none;
    text-align: left;
  }

  .dashboard-chart--large,
  .dashboard-chart--compact {
    height: 260px;
  }
}

@media (max-width: 640px) {
  .dashboard-page__title {
    font-size: 28px;
  }

  .dashboard-stat__value {
    font-size: 34px;
  }
}
</style>
